import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { CalendarClock, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface Props {
  accountId: string;
}

interface AutoSyncConfig {
  interval_hours: number;
  enabled: boolean;
  last_run_at?: string | null;
  next_run_at?: string | null;
  last_status?: string | null;
  allowed_intervals: number[];
}

const fmtRel = (iso?: string | null): string => {
  if (!iso) return "—";
  try {
    const then = new Date(iso).getTime();
    const now = Date.now();
    const diff = then - now;
    const abs = Math.abs(diff);
    const min = 60_000, hr = 3_600_000, day = 86_400_000;
    if (abs < min) return diff > 0 ? "in <1m" : "just now";
    if (abs < hr) return diff > 0 ? `in ${Math.round(abs / min)}m` : `${Math.round(abs / min)}m ago`;
    if (abs < day) return diff > 0 ? `in ${Math.round(abs / hr)}h` : `${Math.round(abs / hr)}h ago`;
    return diff > 0 ? `in ${Math.round(abs / day)}d` : `${Math.round(abs / day)}d ago`;
  } catch {
    return iso;
  }
};

/**
 * AutoSyncControls — compact selector that lets users pick a scheduled
 * auto-sync cadence (Off / Every 6h / 12h / 24h) for an OAuth-connected account.
 * Rendered inside <SyncControls /> alongside the manual Sync-now button.
 */
export const AutoSyncControls: React.FC<Props> = ({ accountId }) => {
  const qc = useQueryClient();
  const cfg = useQuery<AutoSyncConfig>({
    queryKey: ["auto-sync", accountId],
    queryFn: async () => (await api.get(`/platforms/${accountId}/auto-sync`)).data,
  });

  const update = useMutation({
    mutationFn: async (interval_hours: number) =>
      (await api.put(`/platforms/${accountId}/auto-sync`, { interval_hours })).data as AutoSyncConfig,
    onSuccess: (data) => {
      toast.success(
        data.enabled
          ? `Auto-sync every ${data.interval_hours}h · next ${fmtRel(data.next_run_at)}`
          : "Auto-sync disabled",
      );
      qc.setQueryData(["auto-sync", accountId], data);
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.detail || "Could not update auto-sync"),
  });

  const value = cfg.data ? String(cfg.data.interval_hours) : "0";
  const intervals = cfg.data?.allowed_intervals ?? [0, 6, 12, 24];

  return (
    <div
      className="mt-2 flex flex-wrap items-center gap-2 text-xs text-[color:var(--sg-muted)]"
      data-testid={`auto-sync-${accountId}`}
    >
      <div className="flex items-center gap-1.5">
        <CalendarClock className="h-3.5 w-3.5" />
        <span>Auto-sync</span>
      </div>
      <Select
        value={value}
        onValueChange={(v) => update.mutate(parseInt(v, 10))}
        disabled={update.isPending || cfg.isLoading}
      >
        <SelectTrigger
          className="h-7 w-[130px] text-xs"
          data-testid={`auto-sync-select-${accountId}`}
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {intervals.map((h) => (
            <SelectItem key={h} value={String(h)} data-testid={`auto-sync-option-${h}h-${accountId}`}>
              {h === 0 ? "Off" : `Every ${h}h`}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {update.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
      {cfg.data?.enabled && (
        <>
          {cfg.data.next_run_at && (
            <span data-testid={`auto-sync-next-${accountId}`}>
              · Next {fmtRel(cfg.data.next_run_at)}
            </span>
          )}
          {cfg.data.last_run_at && (
            <span
              className={
                cfg.data.last_status && !cfg.data.last_status.startsWith("ok")
                  ? "text-amber-700"
                  : "text-emerald-700"
              }
              data-testid={`auto-sync-last-${accountId}`}
            >
              · Last {fmtRel(cfg.data.last_run_at)}
              {cfg.data.last_status && !cfg.data.last_status.startsWith("ok")
                ? ` (${cfg.data.last_status.slice(0, 40)})`
                : ""}
            </span>
          )}
        </>
      )}
    </div>
  );
};

export default AutoSyncControls;
